import { useState, useEffect, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { FlaskConical } from "lucide-react";
import { resolveItems } from "@/lib/api";

const WOWHEAD_BASE = "https://www.wowhead.com/tbc";

function refreshWowheadTooltips() {
  try {
    if (window.$WowheadPower && window.$WowheadPower.refreshLinks) {
      window.$WowheadPower.refreshLinks();
    }
  } catch (e) { /* silent */ }
}

// Known TBC consumable -> Wowhead item IDs
const CONSUMABLE_ITEM_IDS = {
  // Potions
  "Destruction Potion": 22839,
  "Haste Potion": 22838,
  "Ironshield Potion": 22849,
  "Super Mana Potion": 22832,
  "Super Healing Potion": 22829,
  "Mighty Rage Potion": 13442,
  "Insane Strength Potion": 22828,
  "Heroic Potion": 22837,
  "Fel Mana Potion": 31677,
  "Mad Alchemist's Potion": 34440,
  // Flasks
  "Flask of Pure Death": 22866,
  "Flask of Blinding Light": 22861,
  "Flask of Relentless Assault": 22854,
  "Flask of Mighty Restoration": 22853,
  "Flask of Supreme Power": 13512,
  "Flask of Chromatic Wonder": 33208,
  "Flask of Fortification": 22851,
  "Flask of Distilled Wisdom": 13511,
  "Shattrath Flask of Pure Death": 32898,
  "Shattrath Flask of Blinding Light": 32901,
  "Shattrath Flask of Relentless Assault": 32899,
  "Shattrath Flask of Mighty Restoration": 32900,
  "Shattrath Flask of Fortification": 32897,
  "Unstable Flask of the Beast": 32766,
  "Unstable Flask of the Bandit": 32764,
  "Unstable Flask of the Elder": 32762,
  "Unstable Flask of the Physician": 32763,
  "Unstable Flask of the Soldier": 32765,
  "Unstable Flask of the Sorcerer": 32761,
  // Battle Elixirs
  "Elixir of Major Agility": 22831,
  "Elixir of Healing Power": 22825,
  "Elixir of Major Firepower": 22833,
  "Elixir of Major Shadow Power": 22835,
  "Elixir of Major Frost Power": 22827,
  "Elixir of Major Strength": 22824,
  "Elixir of Mastery": 28104,
  "Elixir of Demonslaying": 9224,
  "Adept's Elixir": 28103,
  "Onslaught Elixir": 28102,
  "Elixir of the Mongoose": 13452,
  // Guardian Elixirs
  "Elixir of Major Defense": 22834,
  "Elixir of Major Fortitude": 32062,
  "Elixir of Major Mageblood": 22840,
  "Elixir of Draenic Wisdom": 32067,
  "Elixir of Ironskin": 22846,
  // Drums
  "Drums of Battle": 29528,
  "Drums of War": 29527,
  "Drums of Restoration": 29529,
  // Runes / Misc
  "Dark Rune": 20520,
  "Demonic Rune": 12662,
  "Flame Cap": 22788,
  "Thistle Tea": 7676,
  "Mana Emerald": 22044,
  "Mana Ruby": 12662,
  "Healthstone": 22105,
  // Engineering
  "Super Sapper Charge": 23827,
  "Goblin Sapper Charge": 10646,
  "Fel Iron Bomb": 23736,
  "Adamantite Grenade": 23737,
  // Bandages
  "Heavy Netherweave Bandage": 21991,
  "Netherweave Bandage": 21990,
  // Food
  "Well Fed": 33052,
  "Fisherman's Feast": 33052,
  "Spicy Crawdad": 27667,
  "Blackened Basilisk": 27657,
  "Grilled Mudfish": 27664,
  "Crunchy Serpent": 27660,
  "Roasted Clefthoof": 27658,
  "Ravager Dog": 27655,
  "Warp Burger": 27659,
  "Talbuk Steak": 27662,
  "Golden Fish Sticks": 27666,
  "Skullfish Soup": 33825,
  "Stormchops": 33866,
  "Broiled Bloodfin": 33867,
  "Charred Bear Kabobs": 27656,
  "Buzzard Bites": 27651,
  // Weapon Enhancements
  "Superior Wizard Oil": 22522,
  "Brilliant Wizard Oil": 20749,
  "Superior Mana Oil": 22521,
  "Brilliant Mana Oil": 20748,
  "Adamantite Weightstone": 23529,
  "Adamantite Sharpening Stone": 23528,
  "Elemental Sharpening Stone": 18262,
  "Fel Weightstone": 23576,
  "Fel Sharpening Stone": 23577,
  // Scrolls
  "Scroll of Agility V": 27498,
  "Scroll of Agility IV": 10309,
  "Scroll of Strength V": 27503,
  "Scroll of Strength IV": 10310,
  "Scroll of Intellect V": 27499,
  "Scroll of Intellect IV": 10308,
  "Scroll of Spirit V": 27501,
  "Scroll of Spirit IV": 10306,
  "Scroll of Stamina V": 27502,
  "Scroll of Stamina IV": 10307,
  "Scroll of Protection V": 27500,
  "Scroll of Protection IV": 10305,
};

// Fuzzy lookup
function getItemId(name) {
  if (CONSUMABLE_ITEM_IDS[name]) return CONSUMABLE_ITEM_IDS[name];
  for (const [key, id] of Object.entries(CONSUMABLE_ITEM_IDS)) {
    if (name.includes(key) || key.includes(name)) return id;
  }
  return null;
}

// Categorize consumables for grouped display
function categorize(name) {
  const n = name.toLowerCase();
  if (n.includes("flask")) return "Flasks";
  if (n.includes("elixir") || n === "adept's elixir" || n === "onslaught elixir") return "Elixirs";
  if (n.includes("potion") || n.includes("healthstone")) return "Potions";
  if (n.includes("well fed") || n.includes("feast") || n.includes("crawdad") || n.includes("basilisk") || n.includes("mudfish") || n.includes("serpent") || n.includes("clefthoof") || n.includes("dog") || n.includes("burger") || n.includes("steak") || n.includes("fish") || n.includes("soup") || n.includes("stormchops") || n.includes("bloodfin") || n.includes("kabob") || n.includes("bites")) return "Food";
  if (n.includes("oil") || n.includes("weightstone") || n.includes("sharpening")) return "Weapon";
  if (n.includes("scroll")) return "Scrolls";
  if (n.includes("drums")) return "Drums";
  return "Other";
}

const CATEGORY_ORDER = ["Flasks", "Elixirs", "Food", "Weapon", "Potions", "Scrolls", "Drums", "Other"];
const CATEGORY_COLORS = {
  "Flasks": "text-purple-400",
  "Elixirs": "text-green-400",
  "Food": "text-amber-400",
  "Weapon": "text-blue-400",
  "Potions": "text-red-400",
  "Scrolls": "text-cyan-400",
  "Drums": "text-orange-400",
  "Other": "text-muted-foreground",
};

export default function ConsumablesTab({ data }) {
  const consumables = data?.consumables || [];
  const ca = data?.consumable_analysis || null;
  const drums = data?.drums_data || null;
  const [iconMap, setIconMap] = useState({});

  // Resolve item icons from Wowhead via the same resolve-items endpoint used by GearTab
  useEffect(() => {
    const itemIds = consumables.map((c) => getItemId(c.name)).filter(Boolean);
    const uniqueIds = [...new Set(itemIds)];
    if (uniqueIds.length === 0) return;

    resolveItems(uniqueIds, [], [])
      .then((res) => {
        setIconMap(res.items || {});
        setTimeout(refreshWowheadTooltips, 500);
      })
      .catch(() => {});
  }, [consumables]);

  useEffect(() => {
    setTimeout(refreshWowheadTooltips, 500);
  }, [iconMap]);

  // Group consumables by category
  const grouped = {};
  for (const c of consumables) {
    const cat = categorize(c.name);
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(c);
  }

  return (
    <div className="mt-4" data-testid="consumables-tab">
      {/* Data Analysis Summary */}
      {ca && (
        <div className="mb-6" data-testid="consumable-analysis-section">
          <h3 className="heading text-lg font-semibold uppercase tracking-tight mb-3">Consumable Analysis</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <AnalysisStat label="Overall Score" value={ca.total_average} />
            <AnalysisStat label="Elixir/Flask" value={ca.elixir_or_flask} />
            <AnalysisStat label="Food Buff" value={ca.food_buff} />
            <AnalysisStat label="Weapon Enh." value={ca.weapon_enhancement} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <AnalysisStat label="Battle Elixir" value={ca.battle_elixir} />
            <AnalysisStat label="Guardian Elixir" value={ca.guardian_elixir} />
            <AnalysisStat label="Flask" value={ca.flask} />
            {ca.scrolls && ca.scrolls !== "0" ? (
              <div className="stat-card">
                <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Scrolls</span>
                <p className="text-lg font-bold font-mono">{ca.scrolls}</p>
              </div>
            ) : (
              <AnalysisStat label="Scrolls" value={0} />
            )}
          </div>
          {ca.suboptimal && ca.suboptimal.length > 0 && (
            <div className="p-3 rounded-sm bg-destructive/10 border border-destructive/20">
              <span className="text-xs font-mono uppercase tracking-wider text-destructive">Suboptimal Consumables</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {ca.suboptimal.map((s, i) => (
                  <Badge key={i} variant="destructive" className="text-xs">{s}</Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Drums section */}
      {drums && drums.total_drums > 0 && (
        <div className="mb-6" data-testid="drums-section">
          <h3 className="heading text-lg font-semibold uppercase tracking-tight mb-3">Drums</h3>
          <div className="grid grid-cols-3 gap-3">
            <div className="stat-card">
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Total Drums</span>
              <p className="text-lg font-bold font-mono">{drums.total_drums}</p>
            </div>
            <div className="stat-card">
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Buffs/Drum</span>
              <p className="text-lg font-bold font-mono">{drums.buffs_per_drum}</p>
            </div>
            <div className="stat-card">
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Score</span>
              <p className="text-lg font-bold font-mono">{drums.weighted_score}</p>
            </div>
          </div>
        </div>
      )}

      {/* Combat Log Consumables — Read Only with Icons */}
      <h3 className="heading text-lg font-semibold uppercase tracking-tight mb-4">
        {ca ? "Combat Log Consumables" : "Consumables"}
      </h3>

      {consumables.length === 0 ? (
        <div className="stat-card text-center py-8">
          <FlaskConical className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No combat log consumable data</p>
          <p className="text-xs text-muted-foreground mt-1">Import a combat log to detect in-fight consumable usage</p>
        </div>
      ) : (
        <div className="space-y-5">
          {CATEGORY_ORDER.filter((cat) => grouped[cat]?.length > 0).map((cat) => (
            <div key={cat}>
              <span className={`text-[11px] font-mono uppercase tracking-widest ${CATEGORY_COLORS[cat]} mb-2 block`}>{cat}</span>
              <div className="space-y-0.5">
                {grouped[cat].map((c, idx) => {
                  const itemId = getItemId(c.name);
                  const resolved = itemId ? iconMap[String(itemId)] : null;
                  const itemUrl = itemId ? `${WOWHEAD_BASE}/item=${itemId}` : null;
                  return (
                    <div key={`${c.name}-${idx}`} data-testid={`consumable-row-${cat.toLowerCase()}-${idx}`}
                      className="flex items-center gap-3 px-3 py-1.5 rounded-sm hover:bg-card/50 transition-colors">
                      {/* Icon */}
                      {resolved?.icon ? (
                        <a href={itemUrl} onClick={(e) => e.preventDefault()} className="shrink-0">
                          <img src={resolved.icon} alt={c.name}
                            className="w-7 h-7 rounded-[3px] border border-border/40 hover:brightness-125 transition-all"
                            loading="lazy" />
                        </a>
                      ) : (
                        <div className="w-7 h-7 rounded-[3px] bg-secondary/50 flex items-center justify-center shrink-0">
                          <FlaskConical className="h-3.5 w-3.5 text-muted-foreground" />
                        </div>
                      )}
                      {/* Name with Wowhead tooltip */}
                      <div className="flex-1 min-w-0">
                        {itemId ? (
                          <a href={itemUrl}
                            data-wowhead={`item=${itemId}&domain=tbc`}
                            onClick={(e) => e.preventDefault()}
                            className="text-sm font-medium text-[#1eff00] hover:brightness-125 cursor-pointer truncate block">
                            {c.name}
                          </a>
                        ) : (
                          <span className="text-sm font-medium truncate block">{c.name}</span>
                        )}
                      </div>
                      {/* Count */}
                      <span data-testid={`consumable-count-${cat.toLowerCase()}-${idx}`}
                        className="text-sm font-mono font-bold tabular-nums text-right text-muted-foreground">
                        {c.count}x
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary */}
      {consumables.length > 0 && (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3">
          <SummaryStat label="Total Used" value={consumables.reduce((s, c) => s + c.count, 0)} />
          <SummaryStat label="Types Used" value={consumables.filter((c) => c.count > 0).length} />
          <SummaryStat label="Categories" value={Object.keys(grouped).length} />
        </div>
      )}
    </div>
  );
}

function SummaryStat({ label, value }) {
  return (
    <div className="stat-card">
      <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{label}</span>
      <p className="text-lg font-bold font-mono">{value}</p>
    </div>
  );
}

function AnalysisStat({ label, value }) {
  if (value === null || value === undefined) {
    return (
      <div className="stat-card">
        <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{label}</span>
        <p className="text-lg font-bold font-mono text-muted-foreground">-</p>
      </div>
    );
  }
  const pct = Math.round(value * 100);
  const color = pct >= 80 ? "text-green-500" : pct >= 50 ? "text-yellow-500" : "text-destructive";
  return (
    <div className="stat-card">
      <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{label}</span>
      <p className={`text-lg font-bold font-mono ${color}`}>{pct}%</p>
    </div>
  );
}
